import React, { useEffect, useState } from "react";
import { Handshake, Phone, Mail, Home, FileText, FileSignature, Check, TrendingUp, TrendingDown, Percent, Save, Wallet, Clock } from "lucide-react";
import { API } from "./api";

const eur = (v) => (v || v === 0) ? Number(v).toLocaleString("fr-FR") + " €" : "—";

export default function MemberComandats() {
  const [items, setItems] = useState(null);
  const [totals, setTotals] = useState({ receive: 0, pay: 0 });
  const [myName, setMyName] = useState("");
  const [signing, setSigning] = useState("");
  const [rates, setRates] = useState({});     // token -> taux édité
  const [busy, setBusy] = useState("");

  const load = () => {
    fetch(`${API}/member/comandats`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d) => {
        setItems(d.items || []); setTotals(d.totals || { receive: 0, pay: 0 }); setMyName(d.my_name || "");
        const rm = {}; (d.items || []).forEach((i) => { rm[i.token] = i.commission_rate ? String(i.commission_rate) : ""; });
        setRates(rm);
      })
      .catch(() => setItems([]));
  };
  useEffect(load, []);

  const sign = async (c) => {
    setSigning(c.token);
    try {
      await fetch(`${API}/comandat/${c.token}/sign`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: c.role === "titulaire" ? "owner" : "partner", name: myName }),
      });
      load();
    } catch (_) {}
    setSigning("");
  };

  // Titulaire : enregistre le taux de commission d'agence → recalcul auto des rétrocommissions.
  const saveRate = async (c) => {
    setBusy(c.token + "-rate");
    try {
      await fetch(`${API}/comandat/${c.token}/split`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ owner_pct: c.split.owner_pct, partner_pct: c.split.partner_pct, commission_rate: rates[c.token] ? Number(rates[c.token]) : null }),
      });
      load();
    } catch (_) {}
    setBusy("");
  };

  const setPayment = async (c, status) => {
    setBusy(c.token + "-pay");
    try {
      await fetch(`${API}/comandat/${c.token}/payment`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }),
      });
      load();
    } catch (_) {}
    setBusy("");
  };

  if (!items) return null;
  if (items.length === 0) return null;

  const hasFees = totals.receive > 0 || totals.pay > 0;

  return (
    <div className="space-y-5 pt-2" data-testid="member-comandats">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-white flex items-center gap-2"><Handshake className="w-5 h-5 text-[#C19A6B]" /> Collaborations acceptées</h2>
        <p className="text-sm text-zinc-400 mt-0.5">{items.length} co-mandat{items.length > 1 ? "s" : ""} accepté{items.length > 1 ? "s" : ""} · rétrocommissions calculées automatiquement</p>
      </div>

      {/* Dashboard rétrocommissions (montants NON encore versés) */}
      {hasFees && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="comandat-retro-dashboard">
          <div className="rounded-xl border border-[#C19A6B]/25 bg-[#C19A6B]/10 p-4 flex items-center gap-3" data-testid="retro-receive">
            <div className="w-10 h-10 rounded-full bg-[#C19A6B]/20 flex items-center justify-center shrink-0"><TrendingUp className="w-5 h-5 text-[#C19A6B]" /></div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-400">À percevoir (non versé)</p>
              <p className="text-2xl font-bold text-[#C19A6B]" data-testid="retro-receive-amount">{eur(totals.receive)}</p>
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-3" data-testid="retro-pay">
            <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center shrink-0"><TrendingDown className="w-5 h-5 text-zinc-300" /></div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-400">À verser aux partenaires</p>
              <p className="text-2xl font-bold text-white" data-testid="retro-pay-amount">{eur(totals.pay)}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
          {items.map((c, i) => {
            const isOwner = c.role === "titulaire";
            const paid = c.payment_status === "paid";
            return (
            <div key={`${c.slug}-${i}`} data-testid={`comandat-card-${i}`} className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
              <div className="flex gap-3 p-4">
                {c.cover
                  ? <img src={c.cover} alt="" className="w-20 h-20 object-cover rounded-lg shrink-0" />
                  : <div className="w-20 h-20 rounded-lg bg-white/5 flex items-center justify-center shrink-0"><Home className="w-6 h-6 text-zinc-600" /></div>}
                <div className="min-w-0 flex-1">
                  <p className="text-white font-medium leading-tight truncate">{c.listing_title}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{c.city}{c.price ? ` · ${Number(c.price).toLocaleString("fr-FR")} €` : ""}</p>
                  <span className={`inline-block mt-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${isOwner ? "bg-blue-500/20 text-blue-300" : "bg-[#C19A6B]/20 text-[#C19A6B]"}`}>
                    Vous êtes {c.role}
                  </span>
                </div>
              </div>
              <div className="px-4 pb-4 space-y-3">
                <div className="rounded-lg bg-[#C19A6B]/10 border border-[#C19A6B]/25 px-3 py-2 flex items-center justify-between">
                  <span className="text-xs text-zinc-300">Votre part d'honoraires</span>
                  <span className="text-lg font-bold text-[#C19A6B]">{c.my_pct}%</span>
                </div>

                {/* Taux de commission — éditable par le titulaire uniquement */}
                {isOwner && (
                  <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2" data-testid={`comandat-rate-block-${i}`}>
                    <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1"><Percent className="w-3 h-3" /> Taux de commission d'agence</p>
                    <div className="flex items-center gap-2">
                      <input type="number" min="0" max="15" step="0.5" value={rates[c.token] ?? ""} placeholder="ex. 5"
                        onChange={(e) => setRates((r) => ({ ...r, [c.token]: e.target.value }))} data-testid={`comandat-rate-input-${i}`}
                        className="w-20 bg-white/[0.04] border border-white/12 rounded-[5px] px-2 py-1.5 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#C19A6B]/60" />
                      <span className="text-xs text-zinc-500">%</span>
                      <button onClick={() => saveRate(c)} disabled={busy === c.token + "-rate"} data-testid={`comandat-rate-save-${i}`}
                        className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-[#C19A6B] border border-[#C19A6B]/40 rounded-[5px] px-2 py-1.5 hover:bg-[#C19A6B]/10 disabled:opacity-50">
                        <Save className="w-3 h-3" /> Enregistrer
                      </button>
                    </div>
                  </div>
                )}

                {/* Détail rétrocommissions de ce co-mandat */}
                {c.fees ? (
                  <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-xs space-y-1" data-testid={`comandat-retro-${i}`}>
                    <div className="flex items-center justify-between text-zinc-400"><span>Commission d'agence{c.commission_rate ? ` (${c.commission_rate}%)` : ""}</span><span className="text-white">{eur(c.fees)}</span></div>
                    <div className="flex items-center justify-between text-zinc-400"><span>Part OFF MARKETTE (2%)</span><span className="text-zinc-300">− {eur(c.off_amount)}</span></div>
                    <div className="flex items-center justify-between border-t border-white/10 pt-1"><span className="text-zinc-400">Net à partager</span><span className="text-white">{eur(c.net_fees)}</span></div>
                    <div className="flex items-center justify-between"><span className="text-zinc-400">À percevoir</span><span className="font-semibold text-[#C19A6B]">{eur(c.receive_amount)}</span></div>
                    {isOwner && <div className="flex items-center justify-between"><span className="text-zinc-400">À verser au partenaire</span><span className="font-semibold text-zinc-200">{eur(c.pay_amount)}</span></div>}
                  </div>
                ) : (
                  <p className="text-[11px] text-zinc-500">{isOwner ? "Indiquez le taux de commission ci-dessus pour calculer les rétrocommissions." : "En attente du taux de commission indiqué par le titulaire."}</p>
                )}

                {/* Suivi du versement */}
                {c.fees ? (
                  <div className="flex items-center justify-between gap-2" data-testid={`comandat-payment-${i}`}>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full ${paid ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                      {paid ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />} {paid ? "Versé" : "En attente de versement"}
                    </span>
                    {isOwner && (
                      <button onClick={() => setPayment(c, paid ? "pending" : "paid")} disabled={busy === c.token + "-pay"} data-testid={`comandat-payment-toggle-${i}`}
                        className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-[5px] transition-colors ${paid ? "border border-white/15 text-zinc-300 hover:bg-white/5" : "bg-[#C19A6B] text-white hover:bg-[#d1af84]"}`}>
                        <Wallet className="w-3 h-3" /> {paid ? "Annuler le versement" : "Marquer comme versé"}
                      </button>
                    )}
                  </div>
                ) : null}

                {c.fees && paid && (
                  <a href={`${API}/comandat/${c.token}/receipt.pdf`} target="_blank" rel="noopener noreferrer" data-testid={`comandat-receipt-${i}`}
                     className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-400 hover:underline">
                    <FileText className="w-3.5 h-3.5" /> Justificatif de versement (PDF)
                  </a>
                )}

                {/* Statut des signatures */}
                <div className="flex flex-wrap gap-2" data-testid={`comandat-signatures-${i}`}>
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${c.owner_signed ? "bg-emerald-500/20 text-emerald-300" : "bg-white/5 text-zinc-500"}`}>
                    {c.owner_signed && <Check className="w-3 h-3" />} Titulaire {c.owner_signed ? "signé" : "en attente"}
                  </span>
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${c.partner_signed ? "bg-emerald-500/20 text-emerald-300" : "bg-white/5 text-zinc-500"}`}>
                    {c.partner_signed && <Check className="w-3 h-3" />} Partenaire {c.partner_signed ? "signé" : "en attente"}
                  </span>
                </div>
                {!c.my_signed && (
                  <button onClick={() => sign(c)} disabled={signing === c.token} data-testid={`comandat-sign-${i}`}
                     className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-[#C19A6B] hover:bg-[#d1af84] text-white py-2.5 rounded-[5px] transition-colors disabled:opacity-50">
                    <FileSignature className="w-3.5 h-3.5" /> {signing === c.token ? "Signature…" : `Signer en tant que ${c.role}`}
                  </button>
                )}
                {c.owner_signed && c.partner_signed && (
                  <p className="text-[11px] text-emerald-400 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Accord signé par les deux parties · PDF envoyé par e-mail</p>
                )}
                {c.token && (
                  <a href={`${API}/comandat/${c.token}/agreement.pdf`} target="_blank" rel="noopener noreferrer" data-testid={`comandat-pdf-${i}`}
                     className="inline-flex items-center gap-1.5 text-xs font-medium text-[#C19A6B] hover:underline">
                    <FileText className="w-3.5 h-3.5" /> Télécharger l'accord de collaboration (PDF)
                  </a>
                )}
                <div className="text-xs text-zinc-400">
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{isOwner ? "Partenaire (acquéreurs)" : "Titulaire du mandat"}</p>
                  <p className="text-white text-sm">{c.partner.name}{c.partner.company ? ` · ${c.partner.company}` : ""}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[#C19A6B]">
                    {c.partner.phone && <a href={`tel:${c.partner.phone}`} className="inline-flex items-center gap-1 hover:underline"><Phone className="w-3 h-3" /> {c.partner.phone}</a>}
                    {c.partner.email && <a href={`mailto:${c.partner.email}`} className="inline-flex items-center gap-1 hover:underline"><Mail className="w-3 h-3" /> {c.partner.email}</a>}
                  </div>
                </div>
              </div>
            </div>
          );})}
        </div>
    </div>
  );
}
